import { useEffect, useRef, useState, useCallback } from "react";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SCRIPT_ID = "google-maps-places";

let loaderPromise = null;

function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.google?.maps?.places?.AutocompleteSuggestion) return Promise.resolve(true);
  if (!GOOGLE_MAPS_KEY) return Promise.resolve(false);
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&v=weekly&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
  return loaderPromise;
}

/**
 * PlacesAutocomplete
 * A styled text input that queries the new Places API programmatically and shows
 * our own suggestion dropdown. Falls back gracefully to a plain input if Google
 * fails to load / API is not enabled.
 *
 * onPick receives: {line1, line2, city, state, pincode, country, full}
 */
export default function PlacesAutocomplete({ value, onChange, onPick, placeholder, "data-testid": testId, className }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    loadGoogleMaps().then((ok) => {
      if (!ok) return;
      try {
        if (window.google?.maps?.places?.AutocompleteSessionToken) {
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
        if (window.google?.maps?.places?.AutocompleteSuggestion) {
          setReady(true);
        }
      } catch (_) { /* silently degrade */ }
    });
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = useCallback(async (text) => {
    if (!ready || !text || text.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      const { AutocompleteSuggestion } = window.google.maps.places;
      const request = {
        input: text,
        includedRegionCodes: ["in"],
        includedPrimaryTypes: ["street_address", "premise", "route", "geocode"],
        sessionToken: sessionTokenRef.current,
      };
      const { suggestions: list } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      setSuggestions(list || []);
      setOpen(true);
    } catch (e) {
      // Silent — Places API may not be enabled on the key's Google Cloud project
      setSuggestions([]);
    }
  }, [ready]);

  const onInput = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 250);
  };

  const pickSuggestion = async (s) => {
    setOpen(false);
    try {
      const place = s.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress", "displayName"],
      });
      const parsed = parseNewPlace(place);
      onPick && onPick(parsed);
      if (parsed.line1) onChange(parsed.line1);
      // rotate session token per Google's guidance
      try {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      } catch (_) { /* ignore */ }
    } catch (e) {
      // graceful degrade — keep whatever user typed
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <input
        value={value}
        onChange={onInput}
        onFocus={() => value && value.length >= 3 && fetchSuggestions(value)}
        placeholder={placeholder}
        data-testid={testId}
        className={className}
        autoComplete="street-address"
      />
      {open && suggestions.length > 0 && (
        <div
          data-testid="places-suggestions"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#FDFBF7",
            border: "1px solid #EBE3D5",
            borderTop: "none",
            zIndex: 30,
            maxHeight: 280,
            overflowY: "auto",
            boxShadow: "0 6px 20px rgba(28,51,38,0.08)",
          }}
        >
          {suggestions.map((s, i) => {
            const p = s.placePrediction;
            const main = p?.mainText?.text || p?.text?.text || "";
            const sec = p?.secondaryText?.text || "";
            return (
              <button
                type="button"
                key={p?.placeId || i}
                onClick={() => pickSuggestion(s)}
                data-testid={`places-suggestion-${i}`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "12px 16px",
                  border: "none",
                  borderBottom: "1px solid #F4EFE6",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: 13,
                  color: "#2C1E16",
                }}
                onMouseDown={(e) => e.preventDefault()} // keep input focus
              >
                <div style={{ fontWeight: 500 }}>{main}</div>
                {sec && <div style={{ color: "#7B6F66", fontSize: 12, marginTop: 2 }}>{sec}</div>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getComp(components, type) {
  const c = components?.find((x) => (x.types || []).some((t) => t === type));
  return c?.longText || c?.long_name || c?.shortText || c?.short_name || "";
}

function parseNewPlace(place) {
  const comps = place.addressComponents || place.address_components || [];
  const streetNumber = getComp(comps, "street_number");
  const route = getComp(comps, "route");
  const premise = getComp(comps, "premise") || getComp(comps, "subpremise") || getComp(comps, "establishment");
  const line1 = [streetNumber, route].filter(Boolean).join(" ") || premise || "";
  const line2 =
    getComp(comps, "sublocality_level_1") ||
    getComp(comps, "sublocality") ||
    getComp(comps, "neighborhood") ||
    "";
  const city =
    getComp(comps, "locality") ||
    getComp(comps, "administrative_area_level_2") ||
    getComp(comps, "administrative_area_level_3") ||
    "";
  const state = getComp(comps, "administrative_area_level_1");
  const pincode = getComp(comps, "postal_code");
  const country = getComp(comps, "country");
  const full = place.formattedAddress || place.formatted_address || "";
  return {
    line1: line1 || full.split(",")[0] || "",
    line2,
    city,
    state,
    pincode,
    country,
    full,
  };
}
